'use client';

import { createContext, Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { AppLayer } from '@/shared/constants';
import { ModalDataProp, ModalProps, UrlModalData } from '@/shared/types';

export const MODAL_QUERY_PARAM = 'modal';
export const REFERRAL_QUERY_PARAM = 'ref';

type ModalRegistry = Record<string, React.ComponentType<ModalProps<UrlModalData>>>;

interface ModalEntry {
    id: string;
    component: React.ComponentType<ModalProps<unknown>>;
    data?: unknown;
    /** Set when this entry was opened because it matched `?modal=<urlKey>`. */
    urlKey?: string;
}

type OpenModalParams<D> = {
    ModalComponent: React.ComponentType<ModalProps<D>>;
} & ModalDataProp<D>;

interface ModalContextType {
    openModal: <D>(params: OpenModalParams<D>) => void;
    closeModal: (id?: string) => void;
}

const ModalContext = createContext<ModalContextType | null>(null);

interface ModalUrlSyncProps {
    registry: ModalRegistry;
    stack: ModalEntry[];
    onKeyChange: (key: string | null, referralCode: string | null) => void;
}

/**
 * Owns every `next/navigation` hook needed for URL-driven modals, so ModalProvider itself
 * never touches useSearchParams()/useRouter() unless a urlModalRegistry is actually passed in.
 * Rendered only when a registry is provided, and only inside a <Suspense> boundary, since
 * useSearchParams() requires one in the App Router.
 */
function ModalUrlSync({ registry, stack, onKeyChange }: ModalUrlSyncProps): null {
    const searchParams = useSearchParams();
    const router = useRouter();
    const rawKey = searchParams.get(MODAL_QUERY_PARAM);
    const key = rawKey && rawKey in registry ? rawKey : null;
    const referralCode = searchParams.get(REFERRAL_QUERY_PARAM);
    const prevUrlKeyRef = useRef<string | null>(null);

    useEffect(() => {
        onKeyChange(key, referralCode);
    }, [key, referralCode, onKeyChange]);

    useEffect(() => {
        const currentUrlKey = stack.find((m) => m.urlKey)?.urlKey ?? null;
        if (prevUrlKeyRef.current && !currentUrlKey) {
            const params = new URLSearchParams(window.location.search);
            params.delete(MODAL_QUERY_PARAM);
            const query = params.toString();
            router.replace(`${window.location.pathname}${query ? `?${query}` : ''}`, { scroll: false });
        }
        prevUrlKeyRef.current = currentUrlKey;
    }, [stack, router]);

    return null;
}

interface ModalProviderProps {
    children: React.ReactNode;
    /**
     * Optional map of URL-openable modal keys to modal components, e.g. `{ login: LoginModal }`.
     * When `?modal=<key>` matches a key here, that modal opens automatically. Omit this prop
     * to opt out of URL-driven modals entirely (no useSearchParams/useRouter calls happen).
     */
    urlModalRegistry?: ModalRegistry;
}

/** Opens modals anywhere in the app (`useModal().openModal`); the ones in the URL modal registry also open from `?modal=<key>`. */
export function ModalProvider({ children, urlModalRegistry }: ModalProviderProps): React.ReactElement {
    const [stack, setStack] = useState<ModalEntry[]>([]);

    const openModal = useCallback(<D,>({ ModalComponent, data }: OpenModalParams<D>) => {
        setStack((prev) => [
            ...prev,
            {
                id: crypto.randomUUID(),
                // The stack holds modals with different data types; each entry keeps its component and data together.
                component: ModalComponent as React.ComponentType<ModalProps<unknown>>,
                data,
            },
        ]);
    }, []);

    const closeModal = useCallback((id?: string) => {
        setStack((prev) => (id ? prev.filter((m) => m.id !== id) : prev.slice(0, -1)));
    }, []);

    const handleUrlKeyChange = useCallback(
        (key: string | null, referralCode: string | null) => {
            if (!urlModalRegistry) {
                return;
            }
            setStack((prev) => {
                const currentUrlEntry = prev.find((m) => m.urlKey);
                if (currentUrlEntry?.urlKey === key) {
                    return prev;
                }
                const withoutCurrent = currentUrlEntry ? prev.filter((m) => m !== currentUrlEntry) : prev;
                if (!key) {
                    return withoutCurrent;
                }
                return [
                    ...withoutCurrent,
                    {
                        id: crypto.randomUUID(),
                        component: urlModalRegistry[key] as React.ComponentType<ModalProps<unknown>>,
                        urlKey: key,
                        data: referralCode ? { referralCode } : undefined,
                    },
                ];
            });
        },
        [urlModalRegistry],
    );

    useEffect(() => {
        const html = document.documentElement;
        if (stack.length > 0) {
            const scrollbarWidth = window.innerWidth - html.clientWidth;
            html.style.overflow = 'hidden';
            html.style.paddingRight = `${scrollbarWidth}px`;
        } else {
            html.style.overflow = '';
            html.style.paddingRight = '';
        }
    }, [stack.length]);

    return (
        <ModalContext.Provider value={{ openModal, closeModal }}>
            {urlModalRegistry && (
                <Suspense fallback={null}>
                    <ModalUrlSync registry={urlModalRegistry} stack={stack} onKeyChange={handleUrlKeyChange} />
                </Suspense>
            )}

            {children}
            {stack.map(({ id, component: Modal, data }, index) => (
                <Modal key={id} data={data} onClose={() => closeModal(id)} zIndex={AppLayer.MODAL + index * 2} />
            ))}
        </ModalContext.Provider>
    );
}

export function useModal(): ModalContextType {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModal must be used inside ModalProvider');
    }
    return context;
}
